import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ContactFormProps {
  onSuccess?: () => void;
  className?: string;
}

export function ContactForm({ onSuccess, className = '' }: ContactFormProps) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('submitting');
    setError(null);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const submission = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      company: formData.get('company') as string,
      message: formData.get('message') as string,
    };

    try {
      const { error: supabaseError } = await supabase
        .from('contact_submissions')
        .insert([submission]);

      if (supabaseError) throw supabaseError;

      setStatus('success');
      if (form) {
        form.reset();
      }
      onSuccess?.();
    } catch (err: any) {
      console.error('Form Submission Error:', err);
      setError('Failed to submit form. Please try again later.');
      setStatus('error');
    }
  }

  return (
    <div className={`bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl ${className}`}>
      {status === 'success' ? (
        <div className="bg-[#5e9bb8]/10 dark:bg-[#5e9bb8]/20 border border-[#5e9bb8]/20 dark:border-[#5e9bb8]/30 rounded-xl p-6 text-center">
          <h2 className="text-xl font-semibold text-[#5e9bb8] dark:text-[#5e9bb8] mb-2">
            Thank you for your interest!
          </h2>
          <p className="text-[#499da8] dark:text-[#499da8]/90">
            We'll be in touch soon.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Name *
            </label>
            <input
              type="text"
              name="name"
              id="name"
              required
              className="block w-full px-4 py-3 rounded-xl border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#5e9bb8] focus:ring-[#5e9bb8] dark:bg-gray-700/50 dark:text-white transition-all duration-300"
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              id="email"
              required
              className="block w-full px-4 py-3 rounded-xl border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#5e9bb8] focus:ring-[#5e9bb8] dark:bg-gray-700/50 dark:text-white transition-all duration-300"
            />
          </div>
          
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Company
            </label>
            <input
              type="text"
              name="company"
              id="company"
              className="block w-full px-4 py-3 rounded-xl border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#5e9bb8] focus:ring-[#5e9bb8] dark:bg-gray-700/50 dark:text-white transition-all duration-300"
            />
          </div>
          
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Message *
            </label>
            <textarea
              name="message"
              id="message"
              required
              rows={4}
              className="block w-full px-4 py-3 rounded-xl border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#5e9bb8] focus:ring-[#5e9bb8] dark:bg-gray-700/50 dark:text-white transition-all duration-300"
            />
          </div>

          {error && (
            <div className="bg-red-50/80 dark:bg-red-900/20 backdrop-blur-sm border border-red-100 dark:border-red-800 p-4 rounded-xl">
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}
          
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-xl text-base font-medium text-white bg-gradient-to-r from-[#5e9bb8] to-[#499da8] hover:from-[#4d8299] hover:to-[#3d8289] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5e9bb8] transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {status === 'submitting' ? (
              'Sending...'
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Send Message
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}